import * as STORAGE_MODULE from '../core/storage';

/**
 * Chargement de la sauvegarde + message de réinitialisation — DECISIONS §E
 * (arbitrage A8) : « la version de sauvegarde change, les anciennes parties
 * sont effacées au chargement avec un message ».
 *
 * La forme exacte du drapeau est livrée par l'agent `src/core` : on accepte
 * `readSave() → { save, reset }` **ou** `wasResetOnLoad() → boolean` à côté du
 * `loadSave()` historique. Tout est détecté à l'exécution ; sans drapeau, le
 * jeu se charge comme avant et aucun message n'est affiché.
 */

/** La sauvegarde telle que `new Game(save)` l'attend, sans nommer son type. */
type SaveLike = NonNullable<ConstructorParameters<typeof import('../core/game').Game>[0]>;

/** La sauvegarde a-t-elle été jetée au chargement ? (fait, non consommable) */
let resetOnLoad = false;
/** Message encore à afficher (consommé par le premier écran qui le montre). */
let noticePending = false;

function storage(): Record<string, unknown> {
  return STORAGE_MODULE as unknown as Record<string, unknown>;
}

/**
 * Charge la sauvegarde et mémorise si le cœur l'a jetée (changement de version).
 * Renvoie la sauvegarde brute, ou `null` — l'appelant la passe à `new Game()`.
 */
export function loadSaveWithNotice(): SaveLike | null {
  const store = storage();
  resetOnLoad = false;
  noticePending = false;

  const readSave = store.readSave;
  if (typeof readSave === 'function') {
    try {
      const out = (readSave as () => unknown)();
      if (out && typeof out === 'object' && 'save' in (out as Record<string, unknown>)) {
        const o = out as { save?: unknown; reset?: unknown };
        resetOnLoad = o.reset === true;
        noticePending = resetOnLoad;
        return (o.save ?? null) as SaveLike | null;
      }
      if (out !== undefined) return (out ?? null) as SaveLike | null;
    } catch {
      return null;
    }
  }

  let save: unknown = null;
  const loadSave = store.loadSave;
  if (typeof loadSave === 'function') {
    try {
      save = (loadSave as () => unknown)() ?? null;
    } catch {
      save = null;
    }
  }
  const wasReset = store.wasResetOnLoad;
  if (typeof wasReset === 'function') {
    try {
      resetOnLoad = (wasReset as () => unknown)() === true;
    } catch {
      resetOnLoad = false;
    }
  }
  noticePending = resetOnLoad;
  return save as SaveLike | null;
}

/**
 * La partie a-t-elle été réinitialisée au chargement ? Valeur **non
 * consommable** : elle décrit le chargement, pas l'affichage (les e2e s'en
 * servent pour distinguer « le cœur ne signale rien » de « le message manque »).
 */
export function wasSaveReset(): boolean {
  return resetOnLoad;
}

/** Message affiché une seule fois : l'écran titre le consomme en l'affichant. */
export function consumeSaveResetNotice(): boolean {
  const was = noticePending;
  noticePending = false;
  return was;
}
